import { Injectable } from '@angular/core';
import { globalUtilsHelper } from '@common-modules/shared/helpers/global-utils-helper';
import { forkJoin, map, Observable, of, switchMap } from 'rxjs';

import { LayerItemV2 } from '../models/layer-item';
import { LayerProtocol, LayerType, MapCssStyle, MapThematicStyle } from '../models/layer-type';
import { IMapFiltrableProperties } from '../models/map-filtrable-properties';
import { MapGenericStyle } from '../models/map-generic-style';
import { MapTooltipParams } from '../models/map-tooltip-params';
import { NativeLayer } from '../models/native-layer';
import { BaseLayerItemService } from './base-layer-item.service';
import { BaseLayerProviderService } from './base-layer-provider.service';
import { GenericMapDataService } from './generic-map-data.service';
import { GenericMapStylesService } from './generic-map-styles.service';
import { LayerItemManagerService } from './layer-item-manager.service';
import { MapLogService } from './map-log.service';

@Injectable()
export class GenericMapService {
  public selectedStep: Date = new Date();

  private _map;

  constructor(
    private readonly _layerItemManagerService: LayerItemManagerService,
    private readonly _genericMapStylesService: GenericMapStylesService,
    private readonly _dataService: GenericMapDataService,
    private readonly _genericMapDataService: GenericMapDataService,
    private readonly _log: MapLogService,
  ) {}

  setMap(map): void {
    this._map = map;
  }

  setSelectedStep(step: Date): void {
    this.selectedStep = step;
  }

  setFilters(layerItem: LayerItemV2): Observable<void> {
    const service = this.getLayerItemService(layerItem);
    const filtrableProperties = this.getFiltrableProperties(layerItem);
    layerItem.filters = service.getFilters(filtrableProperties);
    return of(null);
  }

  setExtraFilters(
    layerItems: LayerItemV2[],
    extraFilters: Partial<IMapFiltrableProperties>,
  ): Observable<LayerItemV2[]> {
    layerItems.forEach((layerItem) => {
      const currentFiltrableProperties = {
        ...(layerItem.currentFiltrableProperties ?? layerItem.originalFiltrableProperties),
        ...extraFilters,
      };

      layerItem.currentFiltrableProperties = currentFiltrableProperties;
      this.setFilters(layerItem);
    });

    return this.buildLayerItems(layerItems);
  }

  setStyles(layerItem: LayerItemV2, isUpdate?: boolean): Observable<LayerItemV2> {
    const service = this.getLayerItemService(layerItem);
    return service.getStyles(layerItem, isUpdate).pipe(
      map((layerStyle: MapGenericStyle) => {
        layerItem.layerStyle = layerStyle;
        this._genericMapStylesService.setStyledIcon(layerItem);
        return layerItem;
      }),
    );
  }

  setLayerVisibility(layerItem: LayerItemV2, visible: boolean): Observable<LayerItemV2> {
    layerItem.isVisible = visible;
    const service = this.getLayerItemService(layerItem);
    service.persistLayerVisibility(layerItem, visible);
    return of(layerItem);
  }

  setResults(
    layerItem: LayerItemV2,
    timeStep: Date,
    nativeLayers: NativeLayer[],
    layerItems: LayerItemV2[],
  ): Observable<LayerItemV2> {
    const service = this.getLayerItemService(layerItem);
    const filtrableProperties = this.getFiltrableProperties(layerItem);
    return service.getResults(filtrableProperties, timeStep).pipe(
      switchMap((results) => this.joinProperties(layerItem, results, timeStep, nativeLayers)),
      map((layerItemJoined: LayerItemV2) => {
        const isThematic = this.isThematic(layerItemJoined);
        if (isThematic) {
          this.refreshStyles(layerItemJoined, layerItems);
        }
        return layerItemJoined;
      }),
    );
  }

  getTooltip(tooltipParams: MapTooltipParams): Observable<HTMLElement> {
    const service = this.getLayerItemService(tooltipParams.layerItem);
    return service.getTooltip(tooltipParams);
  }

  hasLayerId(nativeLayer: NativeLayer, layerItem: LayerItemV2, layerId: string): boolean {
    const service = this.getLayerItemService(layerItem);
    return service.hasLayerId(nativeLayer, layerId);
  }

  persistLayersOrder(layerItems: LayerItemV2[]): void {
    if (!layerItems.length) {
      return;
    }

    const service = this.getLayerItemService(layerItems[0]);
    service.persistLayersOrder(layerItems);
  }

  // TODO: Check the duplicity of isThematic in the future
  private isThematic = (layerItem: LayerItemV2) =>
    layerItem.layerStyle?.isThematic || layerItem.layerDefinition.isThematic;

  private joinProperties(
    layerItem: LayerItemV2,
    attributesGroups: IMapFiltrableProperties[],
    timeStep: Date,
    nativeLayers: NativeLayer[],
  ): Observable<LayerItemV2> {
    return of(true).pipe(
      switchMap(() => {
        const service = this.getLayerItemService(layerItem);
        // It is safer to get the nativeLayers from the map instead of layerItem.nativeLayer.
        const groupLayer = this.getGroupLayerFromMap(layerItem, nativeLayers);

        const count = this.countInnerLayers(groupLayer);
        this._log.log(`Join Properties: Prepare to join ${count} layers for ${groupLayer.pane}`);

        const joins$: Observable<any>[] = [];
        groupLayer.eachLayer((innerLayer: NativeLayer) => {
          let attributes = service.getInnerLayerAttributes(innerLayer, attributesGroups, timeStep);

          const additionalProperties = service.getElementAdditionalProperties(
            innerLayer,
            attributesGroups,
          );

          attributes = new Map([...attributes, ...additionalProperties]);

          const labelsMap$ = service.getLabelPropertiesMap(innerLayer).pipe(
            map((labelProperties) => {
              if (labelProperties) {
                const labels: { [key: string]: any } = {};
                for (const [attributeName, labelInfo] of labelProperties.entries()) {
                  labels[attributeName] = labelInfo;
                }

                this._dataService.setLabelProperties(innerLayer, labels, service.getInnerLayerId);
              }
            }),
          );

          const formattedProperties: { [key: string]: string } = globalUtilsHelper.clone(
            innerLayer.feature.properties,
            true,
          );

          const join$ = service.formatAttributes(attributes, innerLayer).pipe(
            map((formattedAttributes) => {
              if (formattedAttributes) {
                for (const [attributeName, formattedValue] of formattedAttributes.entries()) {
                  innerLayer.feature.properties[attributeName] = attributes.get(attributeName);
                  formattedProperties[attributeName] = formattedValue;
                }

                this._dataService.setFormattedProperties(
                  innerLayer,
                  formattedProperties,
                  service.getInnerLayerId,
                );

                this._dataService.setProperties(
                  innerLayer,
                  globalUtilsHelper.mapToPlainObject(attributes),
                  service.getInnerLayerId,
                );
              }
            }),
          );
          joins$.push(join$);
          joins$.push(labelsMap$);
        });

        const result$ = joins$.length ? forkJoin(joins$).pipe(map(() => layerItem)) : of(layerItem);
        return result$;
      }),
    );
  }

  getGroupLayerFromMap(layerItem: LayerItemV2, nativeLayers: NativeLayer[]): NativeLayer {
    // It is safer to get the nativeLayers from the map instead of layerItem.nativeLayer.
    const layerId = this.getGroupLayerId(layerItem.nativeLayer);

    if (!layerId) {
      return null;
    }

    let layer = nativeLayers.find((nativeLayer) => this.getGroupLayerId(nativeLayer) === layerId);
    layer = layer ?? layerItem.nativeLayer;

    return layer;
  }

  setResultsToLayers(
    layerItems: LayerItemV2[],
    timeStep: Date,
    nativeLayers: NativeLayer[],
  ): Observable<LayerItemV2[]> {
    const layerItems$ = layerItems.map((layerItem) =>
      this.setResults(layerItem, timeStep, nativeLayers, layerItems),
    );
    return forkJoin(layerItems$);
  }

  setNativeLayer(layerItem: LayerItemV2): Observable<LayerItemV2> {
    return this.getNativeLayer(layerItem).pipe(
      map((nativeLayer) => {
        layerItem.nativeLayer = nativeLayer;
        return layerItem;
      }),
    );
  }

  buildLayerItems(layerItems: LayerItemV2[]): Observable<LayerItemV2[]> {
    const layerItems$ = layerItems.map((layerItem) => {
      return forkJoin([this.setFilters(layerItem), this.setStyles(layerItem)]).pipe(
        switchMap(() => this.setNativeLayer(layerItem)),
      );
    });
    return forkJoin(layerItems$);
  }

  applyMapStyles(nativeLayer: NativeLayer, styles: any): void {
    const layerType = nativeLayer?.feature?.geometry?.type;
    if (layerType === LayerType.Polygon || layerType === LayerType.MultiPolygon) {
      styles = {
        ...styles,
        color: '#222',
        weight: 2.5,
      };
    }
    if (nativeLayer?.setStyle && typeof nativeLayer.setStyle === 'function') {
      nativeLayer.setStyle(styles);
    }
  }

  refreshStyles(layerItem: LayerItemV2, layerItems: LayerItemV2[]): LayerItemV2 {
    const stylesFn = this.getStylesFn(layerItem);
    const layer = layerItem.nativeLayer;

    layer.eachLayer((innerLayer: NativeLayer) => {
      const newStyles = stylesFn(innerLayer);
      if (newStyles) {
        const iconPath =
          (layerItem.layerStyle as MapThematicStyle)?.rangeDefinition?.find(
            (range) => range.styleDefinition === newStyles,
          )?.styleDefinition?.definition?.iconPath ??
          layerItem.iconPath ??
          null;

        if (
          iconPath &&
          innerLayer.feature._markerRef &&
          innerLayer.feature.properties.isCircleMarker
        ) {
          const circleStyles = {
            weight: newStyles.definition.Size || 3,
            color: newStyles.definition.Color || newStyles.definition.Fill,
            fillColor: newStyles.definition.Color || newStyles.definition.Fill,
            fillOpacity: newStyles.definition.Opacity || 1,
            pane: layer.pane,
          };

          this.applyMapStyles(innerLayer, circleStyles);
        } else if (
          iconPath &&
          innerLayer.feature._markerRef &&
          innerLayer.feature.properties.isIcon
        ) {
          const updatedIcon = this._genericMapStylesService.buildIcon(iconPath, newStyles);
          const markerRef = innerLayer.feature._markerRef;
          markerRef.setIcon(updatedIcon);
        } else {
          const nativeStyles = this._genericMapStylesService.apiToNativeStyles(
            newStyles.definition,
          );
          this.applyMapStyles(innerLayer, nativeStyles);
        }
      }
    });

    return layerItem;
  }

  private getStylesFn(layerItem: LayerItemV2) {
    const service = this.getLayerItemService(layerItem);
    const stylesFn = this._genericMapStylesService.getStylesFn(
      layerItem.layerStyle,
      layerItem,
      service.getInnerLayerId,
    );
    return stylesFn;
  }

  private getIconPath(layerItem: LayerItemV2, newStyles): string {
    const thematicRangeStyle = (layerItem.layerStyle as MapThematicStyle)?.rangeDefinition?.find(
      (range) => range.styleDefinition === newStyles,
    );

    const iconPath =
      thematicRangeStyle?.styleDefinition?.definition?.iconPath ??
      newStyles?.definition?.iconPath ??
      layerItem.iconPath ??
      null;

    return iconPath;
  }

  getNativeFromLayerItem(nativeLayers: NativeLayer[], layerItem: LayerItemV2): NativeLayer {
    return nativeLayers.find((item) => item.pane === layerItem.pane);
  }

  allLayersLoaded$(layerItems: LayerItemV2[]): Observable<NativeLayer[]> {
    if (!layerItems) {
      return of([]);
    }
    const events$ = layerItems.map((layerItem) => this.getOnLayerLoad(layerItem));
    return forkJoin(events$);
  }

  applyStylesToBaseLayers(layerItems: LayerItemV2[], map): void {
    this.allLayersLoaded$(layerItems).subscribe(() => {
      const nativeLayers = this.getMapNativeLayers();
      const baseLayerItems = layerItems.filter(
        (layerItem) => !layerItem.layerDefinition.isThematic,
      );

      for (const layerItem of baseLayerItems) {
        const groupLayer = this.getGroupLayerFromMap(layerItem, nativeLayers);
        if (groupLayer) {
          groupLayer.eachLayer((innerLayer: NativeLayer) => {
            const newStyles = layerItem.layerStyle.styleDefinition as MapCssStyle;
            const nativeStyles = this._genericMapStylesService.apiToNativeStyles(
              newStyles.definition,
            );
            this.applyMapStyles(innerLayer, nativeStyles);
          });
        }
      }
    });
  }

  getMapNativeLayers(): NativeLayer[] {
    const layers = [];
    this._map.eachLayer((nativeLayer: NativeLayer) => {
      layers.push(nativeLayer);
    });

    return layers;
  }

  private getNativeLayer(layerItem: LayerItemV2): Observable<NativeLayer> {
    const service = this.getLayerProviderService(layerItem);
    switch (layerItem.layerDefinition.layerProtocol) {
      case LayerProtocol.WFS:
        return service.getWFS(layerItem);
      case LayerProtocol.WMS:
        return service.getWMS(layerItem);
    }
  }

  getLayerItemService(layerItem: LayerItemV2): BaseLayerItemService {
    const filtrableProperties = this.getFiltrableProperties(layerItem);
    const service = this._layerItemManagerService.getLayerItemService(filtrableProperties.type);
    return service;
  }

  private getLayerProviderService(layerItem: LayerItemV2): BaseLayerProviderService {
    const service = this._layerItemManagerService.getLayerProviderService(
      layerItem.layerProvider.provider,
    );
    return service;
  }

  private getFiltrableProperties(layerItem: LayerItemV2): IMapFiltrableProperties {
    return layerItem.currentFiltrableProperties ?? layerItem.originalFiltrableProperties;
  }

  getOnLayerLoad(layerItem: LayerItemV2): Observable<NativeLayer> {
    return this.getOnNativeLayerLoad(layerItem.nativeLayer);
  }

  private getOnNativeLayerLoad(
    nativeLayer: NativeLayer,
    timeout = 8 * 1000,
  ): Observable<NativeLayer> {
    return new Observable((subscriber) => {
      // Check if the layer already has features loaded
      let hasFeatures = false;

      // Check if the layer has features by trying to iterate through them
      if (nativeLayer.getLayers && typeof nativeLayer.getLayers === 'function') {
        hasFeatures = nativeLayer.getLayers().length > 0;
      } else if (nativeLayer.eachLayer) {
        nativeLayer.eachLayer(() => {
          hasFeatures = true;
          return false; // Stop after first feature
        });
      }

      // If we have a custom flag that was set on 'load' event previously
      // or if the layer already has features
      if (nativeLayer._loaded === true || hasFeatures) {
        this._log.log('WFST layer already loaded - emitting immediately');
        subscriber.next(nativeLayer);
        subscriber.complete();
        return;
      }

      // If we get here, we need to wait for the load event
      this._log.log('WFST layer not yet loaded - waiting for load event');

      // Set up a one-time event listener for the load event
      const loadHandler = () => {
        // Mark the layer as loaded for future checks
        nativeLayer._loaded = true;
        if (timeoutId) clearTimeout(timeoutId);
        this._log.log('WFST layer loaded by event');
        subscriber.next(nativeLayer);
        subscriber.complete();
      };

      // Set up error handler
      const errorHandler = (e) => {
        if (timeoutId) clearTimeout(timeoutId);
        subscriber.error(e.error || new Error('Failed to load WFST layer'));
      };

      // Set up timeout
      let timeoutId = null;
      if (timeout > 0) {
        timeoutId = setTimeout(() => {
          // Clean up event listeners
          nativeLayer.off('load', loadHandler);
          nativeLayer.off('error', errorHandler);
          subscriber.error(new Error('Timeout waiting for WFST layer to load'));
        }, timeout);
      }

      // Add event listeners
      nativeLayer.once('load', loadHandler);
      nativeLayer.once('error', errorHandler);

      // Return teardown logic
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
        nativeLayer.off('load', loadHandler);
        nativeLayer.off('error', errorHandler);
      };
    });
  }

  private getGroupLayerId = (layer: NativeLayer) => layer?.pane;

  private countInnerLayers(layer: NativeLayer): number {
    let count = 0;
    layer.eachLayer((innerLayer: NativeLayer) => {
      count++;
    });
    return count;
  }

  private getLayerItemFromNative(nativeLayer: NativeLayer, layerItems: LayerItemV2[]): LayerItemV2 {
    const layerItem = layerItems.find((layerItem) => layerItem.pane === nativeLayer.pane);
    return layerItem;
  }
}
